<template>
  <div class="card">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Summary Templates</h2>
      <Button size="small" label="Create New" icon="pi pi-plus" @click="$emit('create')" />
    </div>

    <p class="text-sm text-gray-500 mb-4">
      <strong>System Templates</strong> are built-in and cannot be changed. Clone them to create your own <strong>Custom Templates</strong>.
    </p>

    <DataTable size="small" :value="templates" :loading="loading" stripedRows showGridlines responsiveLayout="scroll">
      <Column field="name" header="Name" sortable></Column>
      <Column field="description" header="Description"></Column>
      <Column header="Type">
        <template #body="{ data }">
          <div class="flex gap-1">
            <Tag :value="data.school ? 'Custom' : 'System'" :severity="data.school ? 'info' : 'secondary'" />
            <Tag v-if="data.properties?.report_mode === 'final_result'" value="Final Result" severity="warn" />
            <Tag v-if="data.properties?.programme_preset && data.properties.programme_preset !== 'custom'"
              :value="data.properties.programme_preset.replace(/_/g, ' ')" severity="secondary" class="!text-[9px]" />
          </div>
        </template>
      </Column>
      <Column header="Actions" :style="{ width: '200px' }">
        <template #body="{ data }">
          <div class="flex gap-2">
            <Button size="small" icon="pi pi-file-export" text rounded
              @click="openReportModal(data)" v-tooltip.top="'Generate Report'" />
            <Button size="small" icon="pi pi-pencil" text rounded severity="secondary"
              v-if="data.school" @click="$emit('edit', data)" v-tooltip.top="'Edit'" />
            <Button size="small" icon="pi pi-copy" text rounded severity="help"
              @click="$emit('clone', data)" v-tooltip.top="'Clone'" />
            <Button size="small" icon="pi pi-trash" text rounded severity="danger"
              v-if="data.school" @click="confirmDelete(data)" v-tooltip.top="'Delete'" />
          </div>
        </template>
      </Column>
    </DataTable>

    <ConfirmDialog />

    <!-- Generate Report Modal -->
    <Dialog v-model:visible="reportModalVisible" modal header="Generate Summary Report"
      :style="{ width: '95vw', maxWidth: '1400px' }" :closable="!reportLoading">

      <!-- Parameter Selection -->
      <div v-if="!reportData">
        <p class="mb-4 text-sm text-gray-600">
          Select context for <strong>{{ selectedTemplate?.name }}</strong>
        </p>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
          <div v-if="isTertiary">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Programme</label>
            <ProgrammeSelect v-model="selectedProgrammeId" />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">{{ labels.CLASS }}</label>
            <ClassroomSectionSelect v-model="selectedClassroomId" :showClassroomsOnly="true" />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Academic Term</label>
            <AcademicCalendarSelect v-model="selectedTermId" size="small" />
          </div>
          <div v-if="isTertiary">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Calculation Method</label>
            <Select v-model="calculationMethod" :options="methodOptions" optionLabel="label" optionValue="value" size="small" class="w-full rounded-lg" />
          </div>
          <div v-if="isTertiary">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Retake Method</label>
            <Select v-model="retakeMethod" :options="retakeMethodOptions" optionLabel="label" optionValue="value" size="small" class="w-full rounded-lg" />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Student Status</label>
            <Select v-model="selectedStudentStatus" :options="studentStatusOptions" optionLabel="label" optionValue="value"
              placeholder="All Students" size="small" class="w-full rounded-lg" showClear />
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <Button size="small" label="Cancel" text @click="reportModalVisible = false" />
          <Button size="small" label="Generate" icon="pi pi-eye" :disabled="!canSubmit" :loading="reportLoading" @click="generateReport" />
        </div>
      </div>

      <!-- Report Results -->
      <div v-else ref="reportContentRef">
        <!-- Meta Header -->
        <div v-if="reportData.meta" class="text-center mb-6 py-3 bg-primary/10 border border-primary/20 rounded-lg print-meta">
          <h3 class="text-lg font-bold uppercase">
            {{ reportData.meta.session }} &mdash; {{ reportData.meta.semester }}
          </h3>
          <p class="text-sm font-semibold uppercase text-gray-600">
            <span v-if="reportData.meta.department_name">{{ reportData.meta.department_name }} &mdash; </span>
            {{ reportData.meta.program_name }}
            <span v-if="reportData.meta.level"> ({{ reportData.meta.level }} Level)</span>
            <span v-if="reportData.meta.calculation_method" class="ml-2 text-primary">[{{ reportData.meta.calculation_method }}]</span>
          </p>
        </div>

        <div class="flex justify-between items-center mb-3 no-print">
          <span class="text-sm text-gray-500">{{ reportData.data.length }} students</span>
          <div class="flex gap-2">
            <Button size="small" label="Print" icon="pi pi-print" text severity="secondary" @click="printReport" />
            <Button size="small" v-if="isTertiary" label="Excel" icon="pi pi-file-excel" text
              :loading="excelLoading" @click="exportExcel" />
            <Button size="small" v-if="isTertiary" label="Download PDF" icon="pi pi-file-pdf" text severity="danger"
              :loading="pdfLoading" @click="exportPdf" />
            <Button size="small" label="Back" icon="pi pi-arrow-left" text @click="reportData = null" />
          </div>
        </div>

        <!-- Main Table -->
        <div class="overflow-x-auto max-h-[50vh] print-table">
          <table class="min-w-full bg-white border border-gray-300 text-sm">
            <thead class="bg-gray-100 text-gray-700 uppercase text-xs font-bold sticky top-0">
              <!-- Final Result: course headers row -->
              <template v-if="reportData.course_headers?.length">
                <tr>
                  <th :colspan="3" class="py-2 px-3 border border-gray-300 text-center bg-gray-200">Student</th>
                  <th :colspan="reportData.course_headers.length" class="py-2 px-3 border border-gray-300 text-center bg-primary/10">Courses</th>
                  <th :colspan="reportData.headers.length - 3" class="py-2 px-3 border border-gray-300 text-center bg-green-50">Summary</th>
                </tr>
                <tr>
                  <th v-for="h in reportData.headers.slice(0, 3)" :key="h.key"
                    class="py-2 px-3 border border-gray-300 text-left whitespace-nowrap">{{ h.label }}</th>
                  <th v-for="ch in reportData.course_headers" :key="'c-'+ch.id"
                    class="py-1 px-1 border border-gray-300 text-center whitespace-nowrap text-[9px]"
                    v-tooltip.top="`${ch.code} — ${ch.title} (${ch.unit}) ${ch.status === 'C' ? 'Compulsory' : ch.status === 'E' ? 'Elective' : ''}`">
                    <div>{{ ch.code }}</div>
                    <div class="text-[7px] text-gray-400">{{ ch.unit }} · {{ ch.status === 'C' ? 'C' : 'E' }}</div>
                  </th>
                  <th v-for="h in reportData.headers.slice(3)" :key="h.key"
                    class="py-2 px-3 border border-gray-300 text-left whitespace-nowrap">{{ h.label }}</th>
                </tr>
              </template>
              <template v-else-if="hasGroupedHeaders">
                <tr>
                  <th v-for="g in headerGroups" :key="g.key"
                    :colspan="g.isGroup ? g.colspan : 1"
                    :rowspan="g.isGroup ? 1 : 2"
                    class="py-2 px-3 border border-gray-300 whitespace-nowrap"
                    :class="g.isGroup ? 'text-center bg-gray-200' : 'text-left'">
                    {{ g.label }}
                  </th>
                </tr>
                <tr>
                  <th v-for="sh in subHeaders" :key="sh.key"
                    class="py-2 px-3 border border-gray-300 text-center whitespace-nowrap text-[10px]">
                    {{ sh.label }}
                  </th>
                </tr>
              </template>
              <tr v-else>
                <th v-for="h in reportData.headers" :key="h.key"
                  class="py-2 px-3 border border-gray-300 text-left whitespace-nowrap">
                  {{ h.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="reportData.data.length === 0">
                <td :colspan="(reportData.course_headers?.length ?? 0) + reportData.headers.length" class="py-8 text-center text-gray-500 italic">
                  <template v-if="reportData.meta?.report_mode === 'final_result'">
                    No students eligible for final result. All students may have outstanding courses — check the semester report.
                  </template>
                  <template v-else>
                    No students found for the selected criteria.
                  </template>
                </td>
              </tr>
              <tr v-for="(row, idx) in reportData.data" :key="idx" class="hover:bg-gray-50 border-b border-gray-100">
                <template v-if="reportData.course_headers?.length">
                  <td v-for="h in reportData.headers.slice(0, 3)" :key="h.key"
                    class="py-1.5 px-3 whitespace-nowrap border border-gray-200">
                    {{ row[h.key] ?? '-' }}
                  </td>
                  <td v-for="ch in reportData.course_headers" :key="'c-'+ch.id"
                    class="py-1 px-1 text-center border border-gray-200 text-xs"
                    :class="row._course_scores?.[ch.id] == null ? 'text-gray-300' : ''">
                    {{ row._course_scores?.[ch.id] != null ? row._course_scores[ch.id] : '-' }}
                  </td>
                  <td v-for="h in reportData.headers.slice(3)" :key="h.key"
                    class="py-1.5 px-3 whitespace-nowrap border border-gray-200"
                    :class="h.calculation_function === 'STATUS' ? statusClass(row[h.key]) : ''">
                    {{ row[h.key] ?? '-' }}
                  </td>
                </template>
                <template v-else>
                  <td v-for="h in reportData.headers" :key="h.key"
                    class="py-1.5 px-3 whitespace-nowrap border border-gray-200"
                    :class="h.calculation_function === 'STATUS' ? statusClass(row[h.key]) : ''">
                    {{ row[h.key] ?? '-' }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Grade Summary Panel (dynamic) -->
        <div v-if="reportData.summary && showGradeSummary" class="mt-6 border border-gray-200 rounded-lg overflow-hidden max-w-3xl mx-auto print-summary">
          <div class="bg-gray-100 p-3 font-semibold text-center border-b border-gray-200">Class Performance Summary</div>
          <div class="flex flex-wrap divide-x divide-gray-200 border-t border-gray-200">
            <div v-for="(count, key) in reportData.summary" :key="key"
              class="flex-1 min-w-[100px] p-3 text-center"
              :class="key === 'fail' ? 'text-red-600' : ''">
              <div class="text-xs text-gray-500 uppercase">{{ formatSummaryLabel(key as string) }}</div>
              <div class="font-bold text-lg">{{ count ?? 0 }}</div>
            </div>
          </div>
        </div>

        <!-- Failed Courses Sub-table -->
        <div v-if="reportData.failed_courses?.length && showFailedCourses" class="mt-6 border border-gray-200 rounded-lg overflow-hidden print-failed">
          <div class="bg-gray-100 p-3 font-semibold text-center border-b border-gray-200">List of Failed Courses</div>
          <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs uppercase border-b border-gray-200">
              <tr>
                <th class="p-3 text-left">Matric No</th>
                <th class="p-3 text-left">Names</th>
                <th class="p-3 text-left text-red-600">Failed Courses</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(fc, i) in reportData.failed_courses" :key="i" class="border-b border-gray-100 hover:bg-red-50">
                <td class="p-3 whitespace-nowrap">{{ fc.matric_no }}</td>
                <td class="p-3 font-medium">{{ fc.names }}</td>
                <td class="p-3 text-red-600">{{ fc.failed_courses }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Legend (dynamic) -->
        <div v-if="showLegend" class="mt-6 border border-gray-200 rounded-lg overflow-hidden max-w-2xl mx-auto print-legend">
          <div class="bg-gray-100 p-3 font-semibold text-center border-b border-gray-200">Interpretation of Terms</div>
          <div class="p-4 text-sm grid grid-cols-2 gap-2">
            <div v-for="(label, key) in legendEntries" :key="key">
              <strong>{{ key }}</strong> — {{ label }}
            </div>
          </div>
        </div>

        <!-- Course Legend (Final Result) -->
        <div v-if="reportData.course_legend?.length" class="mt-4 border border-gray-200 rounded-lg overflow-hidden max-w-3xl mx-auto print-legend">
          <div class="bg-gray-100 p-3 font-semibold text-center border-b border-gray-200">Course Legend</div>
          <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs uppercase border-b"><tr><th class="p-2 text-left">Code</th><th class="p-2 text-left">Title</th></tr></thead>
            <tbody>
              <tr v-for="(c, i) in reportData.course_legend" :key="i" class="border-b border-gray-100">
                <td class="p-2 font-mono">{{ c.code }}</td><td class="p-2">{{ c.title }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Signatures -->
        <div v-if="showSignatures && signatureTitles.length" class="mt-8 print-signatures">
          <div class="flex justify-between gap-8 max-w-3xl mx-auto">
            <div v-for="(sig, i) in signatureTitles" :key="i" class="flex-1 text-center">
              <p class="text-xs font-semibold text-gray-600 mb-8">{{ sig.title }}</p>
              <div class="border-b border-gray-400 mb-1"></div>
              <p v-if="sig.show_sign" class="text-[10px] text-gray-400">Signature</p>
              <div v-if="sig.show_date" class="border-b border-gray-400 mt-4 mb-1"></div>
              <p v-if="sig.show_date" class="text-[10px] text-gray-400">Date</p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import {
  useGetSummaryTemplatesQuery,
  useDeleteSummaryTemplateMutation,
  GetSummaryReportDocument,
  GetSummaryExcelUrlDocument,
} from '@/types/vue-apollo-helpers';
import type { SummaryTemplate, SummaryReport } from '@/types/composition-functions';
import { GET_SUMMARY_PDF_URL } from '@/graphql/Queries/summaryTemplate';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';

const emit = defineEmits(['create', 'edit', 'clone']);

const confirm = useConfirm();
const toast = useToast();
const { isTertiary } = useSchoolFeatures();
const { labels } = useSchoolLabels();
const { levelForId } = useTertiaryLevels();
const { resolveClient } = useApolloClient();

const { result, loading, refetch } = useGetSummaryTemplatesQuery({ fetchPolicy: 'network-only' });
const templates = computed(() => result.value?.summaryTemplates?.data || []);

const { mutate: deleteTemplate } = useDeleteSummaryTemplateMutation();

const confirmDelete = (template: SummaryTemplate) => {
  confirm.require({
    message: `Are you sure you want to delete "${template.name}"?`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        await deleteTemplate({ id: template.id });
        toast.add({ severity: 'success', summary: 'Deleted', detail: 'Template deleted', life: 3000 });
        refetch();
      } catch (e) {
        const err = e as Error;
        toast.add({ severity: 'error', summary: 'Error', detail: err.message, life: 3000 });      }
    },
  });
};

// --- Report Generation ---
const reportModalVisible = ref(false);
const selectedTemplate = ref<SummaryTemplate | null>(null);
const selectedProgrammeId = ref();
const selectedClassroomId = ref();
const selectedTermId = ref();
const calculationMethod = ref('cgpa');
const retakeMethod = ref('sum_all');
const selectedStudentStatus = ref<string | null>(null);
const reportData = ref<SummaryReport | null>(null);
const reportLoading = ref(false);

const methodOptions = [{ label: 'CGPA', value: 'cgpa' }, { label: 'Weighted', value: 'weighted' }];
const retakeMethodOptions = [{ label: 'Sum All', value: 'sum_all' }, { label: 'Best per Course', value: 'best_per_course' }];
const studentStatusOptions = [
  { label: 'Enrolled', value: 'enrolled' },
  { label: 'Graduated', value: 'graduated' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Transferred', value: 'transferred' },
  { label: 'Promoted', value: 'promoted' },
];

const canSubmit = computed(() => {
  if (isTertiary.value) return !!(selectedProgrammeId.value && selectedTermId.value && selectedClassroomId.value);
  return !!(selectedClassroomId.value && selectedTermId.value);
});

const openReportModal = (template: SummaryTemplate) => {
  selectedTemplate.value = template;
  selectedProgrammeId.value = null;
  selectedClassroomId.value = null;
  selectedTermId.value = null;
  calculationMethod.value = template.properties?.calculation_method ?? 'cgpa';
  retakeMethod.value = template.properties?.retake_method ?? 'sum_all';
  selectedStudentStatus.value = null;
  reportData.value = null;
  reportModalVisible.value = true;
};

const generateReport = async() => {
  if (!canSubmit.value || !selectedTemplate.value) return;
  reportLoading.value = true;
  try {
    const client = resolveClient();
    const variables: Record<string, unknown> = {
      templateId: selectedTemplate.value.id,
      calId: selectedTermId.value,
    };
    if (isTertiary.value) {
      variables.programmeId = selectedProgrammeId.value;
      const levelVal = levelForId(selectedClassroomId.value);
      if (levelVal !== null) variables.level = levelVal;
      variables.calculationMethod = calculationMethod.value;
      variables.retakeMethod = retakeMethod.value;
    } else {
      variables.classroomId = selectedClassroomId.value;
    }
    if (selectedStudentStatus.value) {
      variables.student_status = selectedStudentStatus.value;
    }

    const { data } = await client.query({
      query: GetSummaryReportDocument,
      variables,
      fetchPolicy: 'network-only',
    });
    reportData.value = data?.summaryReport ?? null;
    if (!reportData.value) {
      toast.add({ severity: 'warn', summary: 'No Data', detail: 'No report data returned', life: 3000 });
    }
  } catch (e) {
    const error = e as Error;
    toast.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
  } finally {
    reportLoading.value = false;
  }
};

// --- Grouped header logic ---
const hasGroupedHeaders = computed(() =>
  reportData.value?.headers?.some((h: Record<string, unknown>) => h.group) ?? false,
);

const headerGroups = computed(() => {
  if (!reportData.value?.headers) return [];
  const groups: Array<{ label: string; key: string; colspan: number; isGroup: boolean }> = [];
  const headers = reportData.value.headers;
  let i = 0;
  while (i < headers.length) {
    const h = headers[i];
    if (h.group) {
      let count = 1;
      while (i + count < headers.length && headers[i + count].group === h.group) count++;
      groups.push({ label: h.group, key: `group_${i}`, colspan: count, isGroup: true });
      i += count;
    } else {
      groups.push({ label: h.label, key: h.key, colspan: 1, isGroup: false });
      i++;
    }
  }
  return groups;
});

const subHeaders = computed(() =>
  reportData.value?.headers?.filter((h: Record<string, unknown>) => h.group) ?? [],
);

const statusClass = (val: unknown) => {
  if (!val || typeof val !== 'string') return '';
  const s = val.toLowerCase();
  if (s.includes('graduating') && !s.includes('not')) return 'text-primary font-semibold';
  if (s.includes('not') || s.includes('probation')) return 'text-red-600 font-semibold';
  if (s.includes('good')) return 'text-primary';
  return '';
};

const templateProperties = computed(() => selectedTemplate.value?.properties ?? {});
const showGradeSummary = computed(() => templateProperties.value.show_grade_summary !== false);
const showFailedCourses = computed(() => templateProperties.value.show_failed_courses !== false);
const showLegend = computed(() => templateProperties.value.show_legend !== false);
const showSignatures = computed(() => templateProperties.value.show_signatures !== false);
const signatureTitles = computed<Array<{ title: string; show_sign: boolean; show_date: boolean }>>(() =>
  templateProperties.value.signatures ?? [],
);

function formatSummaryLabel(key: string): string {
  const allCategories = [
    ...(templateProperties.value.graduating_categories ?? []),
    ...(templateProperties.value.non_graduating_statuses ?? []),
  ];
  const match = allCategories.find((c: Record<string, unknown>) => c.key === key);
  if (match) return match.label as string;
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const legendEntries = computed<Record<string, string>>(() => {
  const method = reportData.value?.meta?.calculation_method?.toUpperCase() ?? 'CGPA';
  const isWeighted = method === 'WEIGHTED';
  const entries: Record<string, string> = {
    TNUR: 'Total Number of Units Registered',
    TNUP: 'Total Number of Units Passed',
  };
  if (isWeighted) {
    entries['TW'] = 'Total Weight';
    entries['WA'] = 'Weighted Average';
    entries['CWA'] = 'Cumulative Weighted Average';
  } else {
    entries['TCP'] = 'Total Credit Points';
    entries['GPA'] = 'Grade Point Average';
    entries['CGPA'] = 'Cumulative Grade Point Average';
  }
  entries['*Course'] = 'Not yet attempted';
  return entries;
});

const excelLoading = ref(false);

const exportExcel = async() => {
  if (!selectedTemplate.value || !selectedProgrammeId.value || !selectedTermId.value) return;
  excelLoading.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: GetSummaryExcelUrlDocument,
      variables: {
        templateId: selectedTemplate.value.id,
        calId: selectedTermId.value,
        programmeId: selectedProgrammeId.value,
        level: levelForId(selectedClassroomId.value),
        calculationMethod: calculationMethod.value,
        retakeMethod: retakeMethod.value,
      },
      fetchPolicy: 'network-only',
    });
    const result = data?.summaryExcelUrl;
    if (result) {
      toast.add({ severity: 'info', summary: 'Export Queued', detail: 'Your Excel export has been queued. Check the Downloads panel.', life: 5000 });
    }
  } catch (e) {
    const error = e as Error;
    toast.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
  } finally {
    excelLoading.value = false;
  }
};

const pdfLoading = ref(false);
const exportPdf = async() => {
  if (!selectedTemplate.value || !selectedProgrammeId.value || !selectedTermId.value) return;
  pdfLoading.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: GET_SUMMARY_PDF_URL,
      variables: {
        templateId: selectedTemplate.value.id,
        calId: selectedTermId.value,
        programmeId: selectedProgrammeId.value,
        level: levelForId(selectedClassroomId.value),
        calculationMethod: calculationMethod.value,
        retakeMethod: retakeMethod.value,
      },
      fetchPolicy: 'network-only',
    });
    if (data?.summaryPdfUrl) {
      toast.add({ severity: 'info', summary: 'Export Queued', detail: 'Your PDF export has been queued. Check the Downloads panel.', life: 5000 });
    }
  } catch (e) {
    const error = e as Error;
    toast.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
  } finally {
    pdfLoading.value = false;
  }
};

const reportContentRef = ref<HTMLElement | null>(null);

interface SummaryHeader { key: string; label: string; calculation_function?: string | null; }

const buildPrintColgroup = (courseCols: number, summaryHdrs: SummaryHeader[]) => {
  const hasSummary = summaryHdrs.length > 0;
  const snPct = 2.5;
  const matricPct = hasSummary ? 11 : 12;
  const namePct = hasSummary ? 15 : 17;
  const studentPct = snPct + matricPct + namePct;

  // Give text-heavy summary columns more width than numeric ones
  const summaryWidths = summaryHdrs.map(h => {
    const fn = h.calculation_function;
    if (fn === 'STATUS') return 8;
    if (fn === 'FAILED_COURSES') return 7;
    return 3.5;
  });
  const summaryTotalPct = summaryWidths.reduce((a, b) => a + b, 0);

  const courseTotalPct = 100 - studentPct - summaryTotalPct;
  const scoreColPct = courseCols > 0 ? (courseTotalPct / courseCols) : 0;

  let cols = `<col style="width:${snPct}%"><col style="width:${matricPct}%"><col style="width:${namePct}%">`;
  cols += Array(courseCols).fill(`<col style="width:${scoreColPct.toFixed(2)}%">`).join('');
  summaryWidths.forEach(w => { cols += `<col style="width:${w}%">`; });
  return `<colgroup>${cols}</colgroup>`;
};

const printReport = () => {
  if (!reportData.value) return;

  const rd = reportData.value;
  const isFinalResult = !!(rd.course_headers?.length);
  const courseHeaders = rd.course_headers ?? [];
  const studentHeaders = rd.headers.slice(0, 3) as SummaryHeader[];
  const summaryHeaders = rd.headers.slice(3) as SummaryHeader[];
  const pageSize = 'A4 landscape';

  // Max data columns (courses + summary) per page. Student info doesn't count (always repeats).
  const MAX_DATA_COLS_PER_PAGE = 20;

  const esc = (v: unknown) => {
    const s = String(v ?? '-');
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  const metaHtml = rd.meta ? `<div class="print-meta">
    <h3>${esc(rd.meta.session)} &mdash; ${esc(rd.meta.semester)}</h3>
    <p>${rd.meta.department_name ? esc(rd.meta.department_name) + ' &mdash; ' : ''}${esc(rd.meta.program_name)}${rd.meta.level ? ' (' + esc(rd.meta.level) + ' Level)' : ''}</p>
  </div>` : '';

  const studentCells = (row: Record<string, unknown>) =>
    studentHeaders.map(h => `<td class="td-student">${esc(row[h.key])}</td>`).join('');

  const studentHeaderCells = studentHeaders.map(h => `<th class="th-student">${esc(h.label)}</th>`).join('');

  const buildCourseHeaderCells = (chs: typeof courseHeaders) => chs.map(ch =>
    `<th class="th-course"><div>${esc(ch.code)}</div><div class="th-sub">${ch.unit} &middot; ${ch.status === 'C' ? 'C' : 'E'}</div></th>`,
  ).join('');

  const summaryHeaderCells = summaryHeaders.map(h => `<th class="th-summary">${esc(h.label)}</th>`).join('');

  let tablesHtml = '';

  if (isFinalResult && courseHeaders.length > 0) {
    // Build a unified stream of all data columns: courses first, then summary
    type DataCol = { kind: 'course'; header: (typeof courseHeaders)[0] }
                 | { kind: 'summary'; header: SummaryHeader };

    const allDataCols: DataCol[] = [
      ...courseHeaders.map(ch => ({ kind: 'course' as const, header: ch })),
      ...summaryHeaders.map(h => ({ kind: 'summary' as const, header: h })),
    ];

    // Chunk the unified stream at MAX_DATA_COLS_PER_PAGE
    const pageChunks: DataCol[][] = [];
    for (let i = 0; i < allDataCols.length; i += MAX_DATA_COLS_PER_PAGE) {
      pageChunks.push(allDataCols.slice(i, i + MAX_DATA_COLS_PER_PAGE));
    }

    const totalPages = pageChunks.length;

    pageChunks.forEach((chunk, chunkIdx) => {
      const chunkCourses = chunk.filter(c => c.kind === 'course').map(c => c.header) as (typeof courseHeaders);
      const chunkSummary = chunk.filter(c => c.kind === 'summary').map(c => c.header) as SummaryHeader[];
      const colgroup = buildPrintColgroup(chunkCourses.length, chunkSummary);
      const pageLabel = totalPages > 1 ? ` (Page ${chunkIdx + 1} of ${totalPages})` : '';

      // Group header row
      let groupRow = '<tr><th colspan="3" class="th-group bg-student">Student</th>';
      if (chunkCourses.length > 0) {
        groupRow += `<th colspan="${chunkCourses.length}" class="th-group bg-course">Courses${pageLabel}</th>`;
      }
      if (chunkSummary.length > 0) {
        groupRow += `<th colspan="${chunkSummary.length}" class="th-group bg-summary">Summary</th>`;
      }
      groupRow += '</tr>';

      // Column headers
      const colHeaders = buildCourseHeaderCells(chunkCourses)
        + chunkSummary.map(h => `<th class="th-summary">${esc(h.label)}</th>`).join('');

      // Data rows
      const dataRows = rd.data.map(row => {
        const scoreCells = chunkCourses.map(ch => {
          const score = row._course_scores?.[ch.id];
          return `<td class="td-score${score == null ? ' td-empty' : ''}">${score != null ? score : '-'}</td>`;
        }).join('');
        const summaryCells = chunkSummary.map(h => `<td class="td-summary">${esc(row[h.key])}</td>`).join('');
        return `<tr>${studentCells(row)}${scoreCells}${summaryCells}</tr>`;
      }).join('\n');

      tablesHtml += `
        ${metaHtml}
        <table>${colgroup}
          <thead>${groupRow}<tr>${studentHeaderCells}${colHeaders}</tr></thead>
          <tbody>${dataRows}</tbody>
        </table>
        <div class="print-footer">Generated on ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
        ${chunkIdx < totalPages - 1 ? '<div class="page-break"></div>' : ''}`;
    });
  } else {
    // Semester report -- just clone the DOM content (no course column pagination needed)
    const el = reportContentRef.value;
    if (!el) return;
    const cloned = el.cloneNode(true) as HTMLElement;
    cloned.querySelectorAll('.no-print').forEach(n => n.remove());
    tablesHtml = cloned.innerHTML;
  }

  // Build supplementary sections from DOM (summary panel, failed courses, legend, signatures)
  let suppHtml = '';
  if (isFinalResult) {
    const el = reportContentRef.value;
    if (el) {
      const sections = el.querySelectorAll('.print-summary, .print-failed, .print-legend, .print-signatures');
      sections.forEach(s => { suppHtml += (s as HTMLElement).outerHTML; });
    }
  }

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (!printWindow) return;

  printWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Summary Report</title>
<style>
  @page { size: ${pageSize}; margin: 8mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; background: white; }

  .print-meta { text-align: center; padding: 6px 0; margin-bottom: 4px; }
  .print-meta h3 { font-size: 13pt; font-weight: 700; text-transform: uppercase; }
  .print-meta p { font-size: 9pt; font-weight: 600; text-transform: uppercase; color: #4b5563; margin-top: 2px; }

  .page-break { page-break-after: always; height: 0; }

  table { border-collapse: collapse; width: 100%; table-layout: fixed; }
  thead { display: table-header-group; background: #f0f0f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  tbody { display: table-row-group; }
  tr { page-break-inside: avoid; }

  /* Group header row */
  .th-group { padding: 3px 2px; font-size: 7pt; font-weight: 700; border: 0.5px solid #999; text-transform: uppercase; }
  .bg-student { background: #e5e7eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .bg-course { background: #dbeafe !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .bg-summary { background: #dcfce7 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Column headers */
  th { font-weight: 700; padding: 2px 1px; font-size: 6pt;
       border: 0.5px solid #999; text-transform: uppercase; text-align: center;
       white-space: normal; word-break: break-word; vertical-align: bottom; }
  .th-student { text-align: left; font-size: 8pt; padding: 3px 4px; white-space: nowrap; }
  .th-course { font-size: 6.5pt; line-height: 1.2; }
  .th-sub { font-size: 5.5pt; color: #888; font-weight: 600; }
  .th-summary { font-size: 7pt; padding: 2px 3px; white-space: nowrap; }

  /* Data cells */
  td { padding: 2px 2px; font-size: 8pt; font-weight: 600;
       border: 0.5px solid #bbb; white-space: nowrap; text-align: center; }
  .td-student { text-align: left; padding: 2px 4px; overflow: hidden; text-overflow: ellipsis; }
  .td-score { font-size: 8pt; }
  .td-empty { color: #ccc; }
  .td-summary { font-size: 8pt; font-weight: 700; }

  /* Semester report styles (cloned DOM) */
  .overflow-x-auto, .print-table { overflow: visible !important; max-height: none !important; }
  .bg-gray-200, [class*="bg-gray-200"] { background: #e5e7eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  [class*="bg-primary"] { background: #eff6ff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  [class*="bg-green"] { background: #f0fdf4 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .text-green-700 { color: #15803d; }
  .text-red-600, .text-red-700 { color: #dc2626; }
  .text-yellow-700 { color: #a16207; }

  /* Supplementary sections */
  .print-summary, [class*="print-summary"] { margin-top: 10px; page-break-inside: avoid; }
  .print-summary .bg-gray-100 { background: #f3f4f6 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;
                                  padding: 6px; font-weight: 700; text-align: center; font-size: 9pt; border-bottom: 1px solid #d1d5db; }
  .print-summary .flex { display: flex; }
  .print-summary .flex-1 { flex: 1; text-align: center; padding: 6px; min-width: 50px; }
  .print-summary .flex-1 .text-xs { font-size: 7pt; text-transform: uppercase; color: #6b7280; }
  .print-summary .flex-1 .font-bold { font-size: 11pt; font-weight: 700; }
  .divide-x > :not(:first-child) { border-left: 1px solid #e5e7eb; }
  .border-t { border-top: 1px solid #e5e7eb; }

  .print-failed { margin-top: 10px; page-break-inside: avoid; }
  .print-failed table { table-layout: auto !important; }
  .print-failed th, .print-failed td { font-size: 8pt; padding: 4px 8px; text-align: left; white-space: normal; }

  .print-legend { margin-top: 10px; page-break-inside: avoid; }
  .print-legend .bg-gray-100 { background: #f3f4f6 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;
                                 padding: 6px; font-weight: 700; text-align: center; font-size: 9pt; border-bottom: 1px solid #d1d5db; }
  .print-legend .p-4, .print-legend [class*="p-4"] { padding: 8px; }
  .print-legend .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 8pt; }
  .print-legend table { table-layout: auto !important; }
  .print-legend th, .print-legend td { font-size: 8pt; padding: 3px 6px; text-align: left; }

  .print-signatures { margin-top: 28px; page-break-inside: avoid; }
  .print-signatures .flex { display: flex; justify-content: space-between; gap: 32px; max-width: 100%; }
  .print-signatures .flex-1 { flex: 1; text-align: center; }
  .print-signatures .text-xs { font-size: 8pt; font-weight: 600; color: #4b5563; margin-bottom: 28px; }
  .print-signatures .border-b { border-bottom: 1px solid #9ca3af; margin-bottom: 3px; }

  .rounded-lg { border-radius: 6px; overflow: hidden; }
  .border { border: 1px solid #d1d5db; }
  .overflow-hidden { overflow: hidden; }
  .max-w-3xl, .max-w-2xl { max-width: 100%; }
  .mx-auto { margin-left: auto; margin-right: auto; }
  .font-mono { font-family: monospace; }
  .flex-wrap { display: flex; flex-wrap: wrap; }

  .print-footer {
    margin-top: 12px; text-align: center; font-size: 7pt; color: #888;
    padding: 4px 0; border-top: 0.5px solid #ccc;
  }
</style>
</head><body>
${tablesHtml}${suppHtml}</body></html>`);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 300);
};

defineExpose({ refetch });
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
  .print-table {
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>

